import { Link } from "react-router-dom";
import { ArrowRight, Flag, TrendingUp } from "lucide-react";
import { buildGrowthLadder } from "@/lib/growthLadder";

type Field = { value?: unknown } | undefined;
export type CareerJourney = Record<string, Field> | null | undefined;

const DEFAULT_LADDER = [
  "Junior / trainee",
  "Independent practitioner",
  "Senior / specialist",
  "Lead / supervisory",
  "Management or deep-specialist track",
];

const WHAT_CHANGES = [
  "You learn the work under guidance and build your first real results.",
  "You own your work end to end and people trust you to deliver.",
  "You go deep in one area and others come to you for it.",
  "You guide a small team and shape how the work gets done.",
  "You choose: lead people and budgets, or become the go-to expert.",
];

const str = (f: Field) => (typeof f?.value === "string" ? f.value.trim() : "");
const list = (f: Field) =>
  Array.isArray(f?.value) ? (f!.value as unknown[]).filter((x): x is string => typeof x === "string") : [];

function parseLadder(progression: string): string[] | null {
  if (!progression) return null;
  const body = progression.replace(/^typical progression:\s*/i, "").replace(/\.$/, "");
  const steps = body.split(/\s*(?:->|→)\s*/).map((s) => s.trim()).filter(Boolean);
  return steps.length >= 3 ? steps.map((s) => s[0].toUpperCase() + s.slice(1)) : null;
}

/** Which rung this role most often starts on, read from its level label. */
function startRung(level: string | null | undefined): number {
  const l = (level || "").toLowerCase();
  if (/^(executive|director|head|management)/.test(l)) return 4;
  if (/^(lead|principal|manager)/.test(l)) return 3;
  if (/^senior/.test(l)) return 2;
  if (/^(mid|intermediate|experienced)/.test(l)) return 1;
  return 0;
}

export default function GrowthPathway({
  title, level, journey, adjacent, stretch, entrepreneurship, resolve, from,
}: {
  title: string;
  level: string | null | undefined;
  journey: CareerJourney;
  adjacent: string[];
  stretch: string[];
  entrepreneurship?: string | null;
  resolve: (name: string) => string | null;
  from: string;
}) {
  const written = parseLadder(str(journey?.career_progression));
  const built = buildGrowthLadder({ name: title, level: str(journey?.level) || level, stretch, entrepreneurship });
  // Written ladder from the database wins; otherwise the role's own data.
  const ladder: { name: string; note: string; link: boolean }[] = written
    ? written.map((n, i) => ({ name: n, note: WHAT_CHANGES[Math.min(i, WHAT_CHANGES.length - 1)], link: false }))
    : built.length >= 2
      ? built.map((b) => ({ name: b.name, note: b.note, link: b.isRole && b.name !== title }))
      : DEFAULT_LADDER.map((n, i) => ({ name: n, note: WHAT_CHANGES[i], link: false }));
  const start = written ? startRung(str(journey?.level) || level) : 0;
  const onLadder = new Set(ladder.map((l) => l.name));
  const exits = [...new Set([...stretch, ...list(journey?.exit_opportunities)])].filter((n) => !onLadder.has(n)).slice(0, 4);
  const sideways = [...new Set([...adjacent, ...list(journey?.adjacent_careers)])].slice(0, 4);

  const Chip = ({ name }: { name: string }) => {
    const id = resolve(name);
    const cls = "inline-flex min-h-11 items-center gap-1.5 rounded-2xl border border-border bg-muted/40 px-4 text-sm text-foreground";
    return id ? (
      <Link to={`/careers/${id}`} state={{ from }} className={`${cls} hover:bg-secondary/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}>
        {name} <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
      </Link>
    ) : <span className={cls}>{name}</span>;
  };

  return (
    <div className="space-y-4">
      <ol className="rounded-3xl border border-border bg-card p-5 md:p-6" aria-label={`Growth pathway for ${title}`}>
        {ladder.map((step, i) => {
          const here = i === start;
          const past = i < start;
          return (
            <li key={step.name} className="relative flex gap-4 pb-6 last:pb-0">
              {i < ladder.length - 1 && (
                <span aria-hidden="true" className="absolute left-[15px] top-8 bottom-0 w-px bg-border" />
              )}
              <span
                aria-hidden="true"
                className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold tabular-nums ${
                  here ? "border-primary bg-primary text-primary-foreground"
                    : past ? "border-border bg-muted text-muted-foreground"
                    : "border-primary/40 bg-card text-foreground"
                }`}
              >
                {i + 1}
              </span>
              <div className="min-w-0 pt-1">
                <p className="font-semibold text-foreground">
                  {step.link && resolve(step.name) ? (
                    <Link to={`/careers/${resolve(step.name)}`} state={{ from }} className="underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded">
                      {step.name}
                    </Link>
                  ) : step.name}
                  {here && (
                    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-0.5 text-xs font-medium text-primary">
                      <Flag className="h-3 w-3" aria-hidden="true" /> Most people start here
                    </span>
                  )}
                </p>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {step.note}
                </p>
              </div>
            </li>
          );
        })}
      </ol>

      {(sideways.length > 0 || exits.length > 0) && (
        <div className="grid gap-4 md:grid-cols-2">
          {sideways.length > 0 && (
            <div className="rounded-3xl border border-border bg-card p-5">
              <p className="text-sm font-semibold text-foreground">Sideways moves</p>
              <p className="mt-1 text-sm text-muted-foreground">Same skills, a different room.</p>
              <div className="mt-3 flex flex-wrap gap-2">{sideways.map((n) => <Chip key={n} name={n} />)}</div>
            </div>
          )}
          {exits.length > 0 && (
            <div className="rounded-3xl border border-border bg-card p-5">
              <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <TrendingUp className="h-4 w-4 text-primary" aria-hidden="true" /> Step-up moves
              </p>
              <p className="mt-1 text-sm text-muted-foreground">Where experience here can take you next.</p>
              <div className="mt-3 flex flex-wrap gap-2">{exits.map((n) => <Chip key={n} name={n} />)}</div>
            </div>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        A typical path, not a fixed one. Timing depends on you, where you work and what you choose to learn.
      </p>
    </div>
  );
}
